import { Alert, Button, CircularProgress, Skeleton, Stack, Typography } from "@mui/material";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { gateway } from "../gateway";
import { initialBenefitsFetch } from "../permissions/benefits/redux/benefits.init";
import { getEnvVar } from "../utils/get-env";
import { EQueryKeys } from "./subscriptions/hooks/useMySubscriptionInfo.query";

const stripePublishableKey = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") ?? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const currentFixedPlanExpiry = (subscriptionInfo: any): Date | null => {
  const candidates = [subscriptionInfo?.subscription, ...(subscriptionInfo?.subscriptions ?? []).map((item: any) => item?.subscription)].filter(Boolean);
  const expiring = candidates
    .map((subscription: any) => {
      const expDate = subscription?.expDate ? new Date(subscription.expDate) : null;
      const section = subscription?.plan?.product?.section;
      const stillValid = Boolean(expDate && expDate.getTime() > Date.now() && section === "flow_subscription" && subscription?.paypalId !== "free_trial");
      return stillValid ? expDate : null;
    })
    .filter((expDate: Date | null): expDate is Date => Boolean(expDate))
    .sort((a: Date, b: Date) => b.getTime() - a.getTime());
  return expiring[0] ?? null;
};

  const CardForm = ({
  planId,
  chargeLater,
  setErrorMessage,
  setSubscriptionState,
}: {
  planId: string;
  chargeLater: boolean;
  setErrorMessage: (message: string) => void;
  setSubscriptionState: (stateObj: { state: "none" | "success" | "error"; data?: any }) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMessage("");

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (result.error) {
        throw new Error(result.error.message || "The card could not be saved.");
      }

      const paymentMethod = result.setupIntent?.payment_method;
      const paymentMethodId = typeof paymentMethod === "string" ? paymentMethod : paymentMethod?.id;
      if (!paymentMethodId || result.setupIntent?.status !== "succeeded") {
        throw new Error("The card was not confirmed.");
      }

      const response = await gateway.post("/subscriptions/fixed", { planId, paymentMethodId });
      const data = response?.data?.data || {};
      const scheduled = Boolean(data?.scheduled);

      setSubscriptionState({
        state: "success",
        data: {
          message: scheduled
            ? "Your plan is scheduled. The charge happens when your current plan expires."
            : "Subscription activated successfully.",
          scheduled,
          subscriptionId: data?.subscription?.id,
          subscription: data?.subscription || null,
        },
      });

      await queryClient.invalidateQueries({ queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO] });
      dispatch(initialBenefitsFetch() as any);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "The subscription could not be activated.";
      setErrorMessage(message);
      setSubscriptionState({ state: "error", data: { message } });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <PaymentElement />
        <Button type="submit" variant="contained" disabled={!stripe || submitting}>
          {submitting ? <CircularProgress size={22} color="inherit" /> : chargeLater ? "Schedule plan" : "Subscribe"}
        </Button>
      </Stack>
    </form>
  );
};

export const PlanPaymentFixed = ({
  plan,
  setSubscriptionState = () => {},
}: {
  plan?: any;
  setSubscriptionState?: (stateObj: { state: "none" | "success" | "error"; data?: any }) => void;
}) => {
  const { activeCompanyId, company, subscriptionInfo } = useSelector((state: any) => state?.userState || {});
  const companyId = activeCompanyId || company?.id;
  const planId = plan?.originalPlanId || plan?.id;
  const currentPlanExpiresAt = currentFixedPlanExpiry(subscriptionInfo);
  const chargeLater = Boolean(currentPlanExpiresAt) && !plan?.isFreeTrial;
  const expiryLabel = currentPlanExpiresAt
    ? currentPlanExpiresAt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "";
  const [clientSecret, setClientSecret] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!companyId) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const response = await gateway.post("/stripe/vault/setup-intent", { companyId: Number(companyId) });
        const secret = response?.data?.data?.clientSecret || response?.data?.clientSecret;
        if (!secret) throw new Error("Card setup could not be started.");
        if (mounted) setClientSecret(secret);
      } catch (error: any) {
        if (mounted) setErrorMessage(error?.response?.data?.message || error?.message || "Card setup could not be started.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [companyId]);

  if (!stripePromise) {
    return <Alert severity="warning">Card payments are not configured.</Alert>;
  }

  return (
    <Stack spacing={2} sx={{ width: "100%", py: 1 }}>
      {chargeLater && (
        <Alert severity="info">
          Your current plan stays active until {expiryLabel}. The card is saved now and the charge happens automatically when that plan expires.
        </Alert>
      )}
      <Typography variant="body2" color="text.secondary" align="center">
        {chargeLater
          ? "You will not be charged today."
          : plan?.isFreeTrial
            ? "Your card is saved now. The first charge happens when the trial ends."
            : "Your card is charged today, then on the same day each month."}
      </Typography>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      {loading ? (
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
      ) : clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CardForm planId={planId} chargeLater={chargeLater} setErrorMessage={setErrorMessage} setSubscriptionState={setSubscriptionState} />
        </Elements>
      ) : null}
    </Stack>
  );
};
